/* eslint-disable no-restricted-globals */
import React from 'react';
import { makeAutoObservable } from 'mobx';
import { parse } from 'papaparse';
import { ChangeEvent } from 'react';
import { EligibleUsersType } from 'src/domains/casino/webview/bonus-engine/campaign/player-management/eligible-users/EligibleUsers.state';
import { Common } from 'src/AppState/Common';
import { ToasterState } from 'src/AppState/ToasterState';
import { z, ZodError } from 'zod';

class CSVError {
    public constructor(public message: string) {}
}

const EligibleUsersCSVSchema = z.array(
    z
        .object({
            player_id: z.string().transform((val) => {
                const regex = /^\d+$/;
                const parsed = parseInt(val, 10);

                if (regex.test(val) === false || isNaN(parsed)) {
                    throw new CSVError('Column "player_id" can have only number values.');
                } else if (val.length > 15) {
                    throw new CSVError('Player ID must contain no more than 15 digits');
                }

                return parsed;
            }),
        })
        .strict()
);

type EligibleUsersCSVType = z.TypeOf<typeof EligibleUsersCSVSchema>;

export class UploadCSVState {
    private inputRef: HTMLInputElement | null = null;

    public constructor(
        private common: Common,
        private onUpload: (ids: EligibleUsersType) => void
    ) {
        makeAutoObservable(this);
    }

    public readonly allowedMimeTypes: string[] = ['text/csv', 'application/vnd.ms-excel'];
    public readonly allowedFileTypes: string[] = ['.csv'];

    public setInputRef = (inputRef: HTMLInputElement): void => {
        this.inputRef = inputRef;
    };

    public handleClick = (): void => {
        this.inputRef?.click();
    };

    public handleFileDrop = (files: File[]): void => {
        const file = files[0] ?? null;

        if (file !== null) {
            this.parseCSV(file);
        }
    };

    public handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.item(0) ?? null;

        if (file !== null && this.areFileTypesAllowed(file)) {
            this.parseCSV(file);
        } else {
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: `Wrong file type. Only ${this.allowedMimeTypes.toString()} files are allowed.`,
            });
        }
        event.target.value = '';
    };

    private areFileTypesAllowed = (file: File): boolean => {
        return this.allowedMimeTypes.includes(file.type);
    };

    private parseCSV = (file: File): void => {
        parse(file, {
            complete: (fileResult) => {
                try {
                    const result = EligibleUsersCSVSchema.parse(fileResult.data);
                    const playerIdsView = this.formatEligibleUsersFormat(result);
                    this.onUpload(playerIdsView);

                    ToasterState.get(this.common).setToast({
                        variant: 'success',
                        title: 'CSV Uploaded',
                        message: (
                            <>
                                <p>Successfully uploaded {playerIdsView.length} IDs.</p>
                                <p>You have successfully uploaded file "{file.name}"</p>
                            </>
                        ),
                    });
                } catch (error) {
                    const message = this.getErrorMessage(error);

                    ToasterState.get(this.common).setToast({
                        variant: 'error',
                        title: 'Error!',
                        message,
                    });
                }
            },
            header: true,
            skipEmptyLines: true,
        });
    };

    private formatEligibleUsersFormat = (playerIds: EligibleUsersCSVType): EligibleUsersType => {
        return Array.from(new Set(playerIds.map(({ player_id }) => player_id)));
    };

    private getErrorMessage = (error: unknown): string => {
        let message = `Cannot upload CSV file. Please check if data format contains following columns: player_id.`;

        if (error instanceof ZodError) {
            if (error.errors[0]?.code === 'unrecognized_keys') {
                const invalidColumns = error.errors[0]?.keys.reduce((acc, item) => `${acc}"${item}" `);
                message = `${message} The file contains invalid columns: ${invalidColumns}`;
            } else {
                message = `${message} ${error.errors[0]?.message}`;
            }
        }

        if (error instanceof CSVError) {
            message = error.message;
        }

        return message;
    };
}
